import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  PiArrowsInBold,
  PiArrowsOutBold,
  PiMicrophoneBold,
  PiPlayBold,
  PiPlusBold,
  PiSkipBackBold,
  PiSkipForwardBold,
  PiStopBold,
  PiXBold,
} from "react-icons/pi";
import {
  activeRowIndex,
  addSinger,
  bumpSinger,
  dropSinger,
  karaokeRows,
  loadSingers,
  nextSingerId,
  timeKaraokeRows,
  type KaraokeSinger,
  type TimedRow,
} from "../lib/karaoke";
import { karaokeEnvelopeFromUrl } from "../lib/karaokeAudio";
import type { KaraokeBed, TakeItem } from "../lib/takesStore";

type Props = {
  title: string;
  lyrics: string;
  audio: HTMLAudioElement | null;
  queue: TakeItem[];
  activeId: string | null;
  bedReady?: boolean;
  bedSource?: KaraokeBed["source"] | null;
  synced?: boolean;
  making?: boolean;
  onClose: () => void;
  onPlayTake: (take: TakeItem) => void;
  onMakeKaraoke?: () => void;
  onMakeBed?: () => void;
  onSyncLyrics?: () => void;
};

function fmt(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function Karaoke({
  title,
  lyrics,
  audio,
  queue,
  activeId,
  bedReady,
  bedSource,
  synced,
  making,
  onClose,
  onPlayTake,
  onMakeKaraoke,
  onMakeBed,
  onSyncLyrics,
}: Props) {
  const root = useRef<HTMLDivElement | null>(null);
  const activeEl = useRef<HTMLDivElement | null>(null);
  const [now, setNow] = useState(0);
  const [dur, setDur] = useState(0);
  const [paused, setPaused] = useState(true);
  const [wide, setWide] = useState(false);
  const [singers, setSingers] = useState<KaraokeSinger[]>(() => loadSingers());
  const [currentSinger, setCurrentSinger] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [timed, setTimed] = useState<TimedRow[]>(() => timeKaraokeRows(karaokeRows(lyrics), 0));
  const [lockHint, setLockHint] = useState("Matching lines to the mix…");
  const rows = useMemo(() => karaokeRows(lyrics), [lyrics]);
  const active = activeRowIndex(timed, now, dur);
  const askedBed = useRef(false);
  useEffect(() => {
    askedBed.current = false;
  }, [activeId]);
  const qi = queue.findIndex((t) => t.id === activeId);
  const prevTake = qi > 0 ? queue[qi - 1] : null;
  const nextTake = qi >= 0 && qi < queue.length - 1 ? queue[qi + 1] : queue.length > 1 ? queue[0] : null;
  const singer = singers.find((s) => s.id === currentSinger) || singers[0] || null;
  const upNext =
    singers.length > 1
      ? singers[(Math.max(0, singers.findIndex((s) => s.id === singer?.id)) + 1) % singers.length]
      : null;
  const live = useRef({ nextTake, prevTake, singer, singers, onPlayTake, onClose, audio });
  live.current = { nextTake, prevTake, singer, singers, onPlayTake, onClose, audio };

  useEffect(() => {
    if (!currentSinger && singers[0]) setCurrentSinger(singers[0].id);
  }, [currentSinger, singers]);

  useEffect(() => {
    const el = audio;
    if (!el) return;
    const tick = () => {
      setNow(el.currentTime || 0);
      setDur(Number.isFinite(el.duration) ? el.duration : 0);
      setPaused(el.paused || el.ended);
    };
    const ended = () => {
      const n = live.current;
      const nid = nextSingerId(n.singers, n.singer?.id || null);
      if (nid) setCurrentSinger(nid);
      if (n.nextTake) n.onPlayTake(n.nextTake);
    };
    tick();
    el.addEventListener("timeupdate", tick);
    el.addEventListener("play", tick);
    el.addEventListener("pause", tick);
    el.addEventListener("loadedmetadata", tick);
    el.addEventListener("ended", ended);
    el.play().catch(() => undefined);
    return () => {
      el.removeEventListener("timeupdate", tick);
      el.removeEventListener("play", tick);
      el.removeEventListener("pause", tick);
      el.removeEventListener("loadedmetadata", tick);
      el.removeEventListener("ended", ended);
    };
  }, [audio]);

  useEffect(() => {
    activeEl.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [active, title]);

  useEffect(() => {
    const durNow = audio?.duration && Number.isFinite(audio.duration) ? audio.duration : 0;
    const hasLrc = rows.filter((r) => typeof r.cue === "number").length >= 2;
    setTimed(timeKaraokeRows(rows, durNow));
    if (hasLrc || synced) {
      setLockHint("Lines locked from Whisper sync (LRC cues).");
      return;
    }
    const src = audio?.currentSrc || audio?.src || "";
    if (!src) {
      setLockHint("No audio yet — Sync lyrics on a sung take, or Make karaoke.");
      return;
    }
    let cancel = false;
    setLockHint("Matching lines to the mix…");
    void karaokeEnvelopeFromUrl(src)
      .then((hit) => {
        if (cancel || !hit) return;
        setTimed(timeKaraokeRows(rows, hit.duration || durNow, hit.env));
        setLockHint("Section timing from the mix. Sync lyrics for Whisper lock.");
      })
      .catch(() => {
        if (!cancel) setLockHint("Section timing only. Sync lyrics on a sung take.");
      });
    return () => {
      cancel = true;
    };
  }, [rows, audio, title, bedReady, synced]);

  useEffect(() => {
    if (askedBed.current) return;
    if (bedReady || bedSource === "gen" || !onMakeBed || making) return;
    if (!audio?.src && !audio?.currentSrc) return;
    askedBed.current = true;
    onMakeBed();
  }, [bedReady, bedSource, onMakeBed, making, audio]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement;
      if (e.key === "Escape") {
        e.preventDefault();
        void exitWide();
        live.current.onClose();
        return;
      }
      if (typing) return;
      if (e.key === " " || e.key === "k") {
        e.preventDefault();
        const el = live.current.audio;
        if (!el) return;
        if (el.paused) el.play().catch(() => undefined);
        else el.pause();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (live.current.nextTake) live.current.onPlayTake(live.current.nextTake);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (live.current.prevTake) live.current.onPlayTake(live.current.prevTake);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onFs = () => setWide(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const togglePlay = () => {
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => undefined);
    else audio.pause();
  };

  const exitWide = async () => {
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch {
        /* */
      }
    }
  };

  const goWide = async () => {
    try {
      if (!document.fullscreenElement) await root.current?.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      /* */
    }
  };

  const submitSinger = () => {
    setSingers((cur) => {
      const next = addSinger(cur, draft);
      if (!currentSinger && next[0]) setCurrentSinger(next[0].id);
      return next;
    });
    setDraft("");
  };

  return createPortal(
    <div ref={root} className="mf-karaoke" role="dialog" aria-modal="true" aria-label="Karaoke">
      <header className="mf-karaoke-head">
        <strong className="mf-karaoke-brand">
          <PiMicrophoneBold /> Karaoke
        </strong>
        <span className="mf-karaoke-title">{title || "Untitled"}</span>
        {singer ? (
          <span className="mf-karaoke-now">
            Now {singer.name}
            {upNext ? <span> · next {upNext.name}</span> : null}
          </span>
        ) : (
          <span className="mf-karaoke-now muted">Add singers for game night</span>
        )}
        <div className="mf-karaoke-head-acts">
          {onMakeKaraoke ? (
            <button
              type="button"
              className="mf-btn write"
              disabled={making}
              title="Generate a music-only take from Mixing bowl + lyrics. Same seed if a take is open."
              onClick={onMakeKaraoke}
            >
              {making ? "Making karaoke…" : "Make karaoke"}
            </button>
          ) : null}
          {onSyncLyrics ? (
            <button
              type="button"
              className="mf-btn ghost"
              disabled={making}
              title="Whisper word times matched to this take's lyric sheet. Run on a sung take."
              onClick={onSyncLyrics}
            >
              {making ? "Syncing…" : synced ? "Re-sync lyrics" : "Sync lyrics"}
            </button>
          ) : null}
          {onMakeBed && bedSource !== "gen" ? (
            <button
              type="button"
              className="mf-btn ghost"
              disabled={making}
              title="DSP: duck the centered lead vocal on this take. Does not replace the original."
              onClick={onMakeBed}
            >
              {bedReady ? "Remake music only" : making ? "Stripping vocal…" : "Music only"}
            </button>
          ) : null}
          <button type="button" className="mf-btn ghost" onClick={() => void goWide()}>
            {wide ? <PiArrowsInBold /> : <PiArrowsOutBold />} {wide ? "Window" : "Full screen"}
          </button>
          <button
            type="button"
            className="mf-btn ghost"
            onClick={() => {
              void exitWide();
              onClose();
            }}
          >
            <PiXBold /> Exit
          </button>
        </div>
      </header>

      <div className="mf-karaoke-stage">
        {timed.length ? (
          timed.map((row, i) => (
            <div
              key={row.id}
              ref={i === active ? activeEl : undefined}
              className={`mf-karaoke-row ${row.kind}${i === active ? " on" : ""}`}
            >
              {row.kind === "line"
                ? row.text
                : row.kind === "rest"
                  ? `${row.label} — music only`
                  : row.label}
            </div>
          ))
        ) : (
          <p className="mf-karaoke-empty">No lyrics on this take. Write lyrics in step 2, then Make karaoke.</p>
        )}
      </div>

      <div className="mf-karaoke-rail">
        <form
          className="mf-karaoke-singers"
          onSubmit={(e) => {
            e.preventDefault();
            submitSinger();
          }}
        >
          <span className="mf-label">Singers</span>
          {singers.map((s) => (
            <span key={s.id} className={`mf-karaoke-chip${s.id === singer?.id ? " on" : ""}`}>
              <button type="button" onClick={() => setCurrentSinger(s.id)}>
                {s.name} <b>{s.points}</b>
              </button>
              <button
                type="button"
                className="mf-karaoke-x"
                aria-label={`Remove ${s.name}`}
                onClick={() => {
                  setSingers((cur) => dropSinger(cur, s.id));
                  if (currentSinger === s.id) setCurrentSinger(null);
                }}
              >
                ×
              </button>
            </span>
          ))}
          <input
            className="mf-input"
            value={draft}
            maxLength={24}
            placeholder="Add singer"
            aria-label="Add singer"
            onChange={(e) => setDraft(e.target.value)}
          />
          <button type="submit" className="mf-btn ghost" disabled={!draft.trim()}>
            <PiPlusBold /> Add
          </button>
          {singer ? (
            <button
              type="button"
              className="mf-btn"
              onClick={() => setSingers((cur) => bumpSinger(cur, singer.id))}
            >
              +1 {singer.name}
            </button>
          ) : null}
        </form>
        {queue.length > 1 ? (
          <div className="mf-karaoke-queue" role="list" aria-label="Karaoke queue">
            {queue.map((t) => (
              <button
                key={t.id}
                type="button"
                role="listitem"
                className={`mf-karaoke-chip${t.id === activeId ? " on" : ""}`}
                onClick={() => onPlayTake(t)}
              >
                {t.title}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <footer className="mf-karaoke-foot">
        <button type="button" className="mf-btn ghost" disabled={!prevTake} onClick={() => prevTake && onPlayTake(prevTake)}>
          <PiSkipBackBold /> Prev
        </button>
        <button type="button" className="mf-btn primary" onClick={togglePlay} disabled={!audio}>
          {paused ? <PiPlayBold /> : <PiStopBold />} {paused ? "Play" : "Pause"}
        </button>
        <button type="button" className="mf-btn ghost" disabled={!nextTake} onClick={() => nextTake && onPlayTake(nextTake)}>
          Next <PiSkipForwardBold />
        </button>
        <span className="mf-karaoke-time">
          {fmt(now)} / {fmt(dur)}
        </span>
        <span className="mf-karaoke-now muted" title={lockHint}>
          {bedSource === "gen" ? "Generated bed" : bedReady ? "DSP bed" : "Sung take"}
          {" · "}
          {synced ? "Whisper-synced" : "Section timing"}
          {" · "}
          {lockHint}
        </span>
        <input
          type="range"
          min={0}
          max={Math.max(1, dur)}
          step={0.1}
          value={Math.min(now, dur || 0)}
          aria-label="Seek"
          disabled={!audio || dur <= 0}
          onChange={(e) => {
            if (!audio) return;
            audio.currentTime = Number(e.target.value);
            setNow(audio.currentTime);
          }}
        />
      </footer>
    </div>,
    document.body,
  );
}
